// Claude: full buyer renewal modal — plan change, payment record, credits settlement 21-04-2026
// Claude: title → "Subscription"; credits from endpoint; paidAmount/creditsUtilized/planAmount 25-Apr-2026
import { Box, Button, Flex, Input, Text } from "@chakra-ui/react";
import { Modal, Select } from "antd";
import React, { useEffect, useState } from "react";
import { AdminBuyerAPI } from "../../../Apis/adminBuyerAPI";

export interface RenewData {
  subscriptionPlanId: string;
  date: string;
  paymentReference: string;
  planName: string;
  planAmount: string;
  paidAmount: string;
  creditsUtilized: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  buyer: any;
  plansData: any[];
  onConfirm: (data: RenewData) => Promise<void>;
  loading?: boolean;
}

const BuyerRenewModal = ({ isOpen, onClose, buyer, plansData, onConfirm, loading }: Props) => {
  const [formData, setFormData] = useState<RenewData>({
    subscriptionPlanId: "", date: "", paymentReference: "",
    planName: "", planAmount: "", paidAmount: "", creditsUtilized: "",
  });
  const [creditBalance, setCreditBalance] = useState<number>(0);

  useEffect(() => {
    if (isOpen && buyer?.buyerId) {
      // Claude: pre-populate plan fields from plansData so admin doesn't need to re-select 25-Apr-2026
      const preselectedPlan = plansData.find((p: any) => p._id === buyer?.subscriptionPlanId);
      setFormData({
        subscriptionPlanId: buyer?.subscriptionPlanId ?? "",
        date: "", paymentReference: "",
        planName: preselectedPlan?.planName ?? "",
        planAmount: preselectedPlan?.subscriptionFee != null ? String(preselectedPlan.subscriptionFee) : "",
        paidAmount: "", creditsUtilized: "",
      });
      AdminBuyerAPI.getCreditBalance(buyer.buyerId)
        .then((res: any) => setCreditBalance(res?.data?.balance ?? 0))
        .catch(() => setCreditBalance(0));
    }
  }, [isOpen, buyer]);

  const currentPlan = plansData.find((p: any) => p._id === buyer?.subscriptionPlanId);
  const selectedPlan = plansData.find((p: any) => p._id === formData.subscriptionPlanId);
  const currency = selectedPlan?.currency ?? currentPlan?.currency ?? "";

  const set = (key: keyof RenewData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData(p => ({ ...p, [key]: e.target.value }));

  // Claude: auto-populate planName and planAmount when plan selected 25-Apr-2026
  const handlePlanChange = (v: string) => {
    const plan = plansData.find((p: any) => p._id === v);
    setFormData(p => ({
      ...p,
      subscriptionPlanId: v ?? "",
      planName: plan?.planName ?? "",
      planAmount: plan?.subscriptionFee != null ? String(plan.subscriptionFee) : "",
    }));
  };

  return (
    <Modal title="Subscription" open={isOpen} onCancel={onClose} footer={null} destroyOnClose width={520}>
      <Box pt={2}>
        <Flex gap={4} mb={4} p={3} bg="gray.50" borderRadius="md" flexWrap="wrap">
          <Text fontSize="xs"><b>Current Plan:</b> {currentPlan?.planName ?? "None"}</Text>
          {creditBalance > 0 && (
            <Text fontSize="xs" color="orange.600">
              <b>Outstanding Credits:</b> {currency} {creditBalance.toFixed(2)}
            </Text>
          )}
        </Flex>

        <Box mb={3}>
          <Text fontSize="xs" mb={1} fontWeight="medium">New Plan (leave as current to renew same plan)</Text>
          <Select
            style={{ width: "100%", fontSize: "12px" }}
            value={formData.subscriptionPlanId || undefined}
            onChange={handlePlanChange}
            options={plansData.map((p: any) => ({ label: p.planName, value: p._id }))}
            allowClear
            placeholder="Select plan"
          />
        </Box>

        <Flex gap={2} flexWrap="wrap" mb={3}>
          <Box flex={1} minW="120px">
            <Text fontSize="xs" mb={1}>Date</Text>
            <Input type="date" height="30px" fontSize="xs" value={formData.date} onChange={set("date")} />
          </Box>
          <Box flex={2} minW="160px">
            <Text fontSize="xs" mb={1}>Payment Reference</Text>
            <Input height="30px" fontSize="xs" placeholder="Ref" value={formData.paymentReference} onChange={set("paymentReference")} />
          </Box>
        </Flex>

        <Flex gap={2} flexWrap="wrap" mb={4}>
          <Box flex={1} minW="100px">
            <Text fontSize="xs" mb={1}>Plan Amount {currency ? `(${currency})` : ""}</Text>
            <Input height="30px" fontSize="xs" placeholder="0" value={formData.planAmount} onChange={set("planAmount")} />
          </Box>
          <Box flex={1} minW="100px">
            <Text fontSize="xs" mb={1}>Credits Utilized {currency ? `(${currency})` : ""}</Text>
            <Input height="30px" fontSize="xs" placeholder="0" value={formData.creditsUtilized} onChange={set("creditsUtilized")} />
          </Box>
          <Box flex={1} minW="100px">
            <Text fontSize="xs" mb={1}>Paid Amount {currency ? `(${currency})` : ""}</Text>
            <Input height="30px" fontSize="xs" placeholder="0" value={formData.paidAmount} onChange={set("paidAmount")} />
          </Box>
        </Flex>

        <Flex gap={2} justifyContent="flex-end">
          <Button h="30px" fontSize="xs" variant="outline" onClick={onClose}>Cancel</Button>
          <Button h="30px" fontSize="xs" bg="#0E122F" color="white" _hover={{ bg: "#2E7D32" }} isLoading={loading} onClick={() => onConfirm(formData)}>
            Confirm
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
};

export default BuyerRenewModal;
